/**
 * throughputProjection.js — Phase 5 Intelligence Layer.
 *
 * Projects team weekly throughput (items/week) with a confidence band
 * derived from per-annotator weekly-output variance.
 *
 * Inputs: array of annotator velocities (from computeAnnotatorVelocities)
 * Output: Projection { point, low, high, confidence, method, sampleSize,
 *         unit: 'items/week', computedAt, reason? }
 *
 * Strategy:
 *   - Samples = each active annotator's weeklyOutput
 *   - Point = sum (team total) — but the BAND comes from variance ×
 *     sqrt(N) to propagate SEM into a sum
 *   - R13 LOW-2 audit fix (docstring accuracy): always uses Gaussian-SEM
 *     with Student-t critical value at small n (<30). The percentile
 *     method is reserved for Phase 6 — it requires proper bootstrap, not
 *     the naive "percentile × N" scaling (percentile of a sum ≠ sum of
 *     percentiles). See the "Band approach" comment block in
 *     `projectThroughput` (above the `stdev(samples)` call) for the full
 *     Gaussian-SEM-vs-bootstrap rationale. R19 LOW audit fix: block-name
 *     ref instead of line numbers.
 *
 * Edge cases:
 *   - 0 annotators → insufficient_data
 *   - 1 annotator → point only, no band (single sample)
 *   - All weeklyOutput = 0 → insufficient_data with reason
 *
 * Pure function. No DB, no Date.now side effects (computedAt is the
 * only Date, captured at call time).
 */

import { truncate, stdev, CORRELATION_INFLATION_FACTOR, effectiveCritValue } from '../statistics';
import { classifyConfidence, fromStatsBand, insufficientData } from './confidenceBand';

// One-sided z-score for 80% confidence (Φ⁻¹(0.9)). Same value used in
// deadlineProjection.js. Phase 11 slot 1 (Invariant #16).
const Z_SCORE_80_PERCENT_ONE_SIDED = 1.282;
// Tolerance when comparing PROJECTION_BAND_Z against the only-supported value.
const Z_SCORE_TOLERANCE = 0.01;

// R13 LOW-3 audit fix: removed the duplicated STUDENT_T_80 table and the
// local `studentTCorrectedCrit` wrapper. Previously two copies of the same
// t-table lived here and in statistics.js — a silent-drift hazard (R12
// exported `effectiveCritValue` making this duplication unnecessary).
// Now both band construction (below) and sigmaFromBand's back-computation
// route through the single canonical `effectiveCritValue` in statistics.js.

/**
 * @param {Array<{weeklyOutput: number}>} velocities — from computeAnnotatorVelocities
 * @param {number} weeksOfHistory — how much observational data we have (affects confidence)
 * @param {object} config — PROJECTION_* constants from enums.js
 * @param {Date} [now] — R2 LOW-5: inject time for test stability / React identity
 * @returns {Projection}
 */
export function projectThroughput(velocities, weeksOfHistory, config, now = new Date()) {
  // R2 HIGH-4 audit fix (defense-in-depth): fail loud on misconfigured
  // PROJECTION_BAND_Z. Previously a missing/non-finite Z produced NaN
  // bounds that, at the time, slipped past validateProjection's
  // null-symmetry check (NaN != null). validateProjection now rejects
  // non-finite bounds too, but we still want a clearer error here.
  if (config == null || !Number.isFinite(config.PROJECTION_BAND_Z) || config.PROJECTION_BAND_Z <= 0) {
    throw new Error(`projectThroughput: config.PROJECTION_BAND_Z must be positive finite (got ${config?.PROJECTION_BAND_Z})`);
  }
  // R3 M1 audit fix: Student-t table only covers the 80% band (z=1.282).
  // If a caller sets PROJECTION_BAND_Z to a different value (e.g. 1.645
  // for 90%), the t-correction branch silently skips — producing a band
  // ~30% too narrow at small n. Fail loud so config drift can't silently
  // under-cover. Extending the t-table to multiple bands is a Phase 6
  // decision if a customer requests it.
  if (Math.abs(config.PROJECTION_BAND_Z - Z_SCORE_80_PERCENT_ONE_SIDED) > Z_SCORE_TOLERANCE) {
    throw new Error(
      `projectThroughput: only the nominal 80% band (z=1.282) is supported. Received PROJECTION_BAND_Z=${config.PROJECTION_BAND_Z}. ` +
      `Student-t correction for small-n samples is only calibrated for z≈1.282. ` +
      `Either keep PROJECTION_BAND_Z at 1.282 or extend STUDENT_T tables for the target band.`
    );
  }
  if (!Array.isArray(velocities) || velocities.length === 0) {
    return insufficientData('items/week', 'no active annotators', now);
  }

  // M2 audit fix: new-hires with 0 hours have weeklyOutput = 0 (because
  // annotationsPerHour = 0 when hours = 0). Including them in the sample
  // pulls the team mean toward 0 and inflates stdev. Exclude annotators
  // flagged 'new_no_data' from the stats (their presence is captured by
  // velocities.length for confidence classification, not stats).
  const usableVelocities = velocities.filter((v) => v.flag !== 'new_no_data');
  const samples = usableVelocities
    .map((v) => (typeof v.weeklyOutput === 'number' && Number.isFinite(v.weeklyOutput) ? v.weeklyOutput : null))
    .filter((v) => v != null);

  if (samples.length === 0) {
    return insufficientData('items/week', 'no throughput samples', now);
  }

  // All-zero samples — annotators are assigned but producing nothing.
  // Not zero confidence; it's a valid zero-rate projection, but flag it.
  if (samples.every((s) => s === 0)) {
    const p = {
      point: 0, low: 0, high: 0,
      confidence: 'low',
      method: 'gaussian',
      sampleSize: samples.length,
      unit: 'items/week',
      computedAt: now,
      reason: 'zero throughput — no items produced in window',
    };
    return p;
  }

  const teamPoint = samples.reduce((acc, v) => acc + v, 0);
  const confidence = classifyConfidence(velocities.length, weeksOfHistory, config);

  // Band approach: SE of a sum of N independent samples = stdev × sqrt(N).
  // We use Gaussian-SEM throughout — a percentile approach would need
  // proper bootstrap (sampling N from the distribution many times) not a
  // naive "percentile × N" scaling (which is mathematically wrong — the
  // percentile of a sum is not the sum of percentiles). Bootstrap can be
  // added later; Gaussian SEM is the defensible v1 approach.
  const s = stdev(samples);
  if (s == null) {
    // n = 1 — can't compute stdev; point only, null band
    return {
      point: teamPoint, low: null, high: null,
      confidence, method: 'gaussian',
      sampleSize: samples.length, unit: 'items/week',
      computedAt: now,
    };
  }

  // Phase 5.5 R3 audit fixes:
  //   H2a — Student-t critical value (not just z) for small-n samples.
  //         At n=3, t₀.₁₀,₂ = 1.886 vs z = 1.282 — ~47% wider band,
  //         which is the honest representation.
  //   H2b — correlation inflation factor on the stdev of the sum. Real
  //         same-project annotator samples are not independent (they
  //         share tool outages, backlog crunches, deadline pressure).
  //         Without longitudinal covariance data we inflate by a
  //         conservative multiplicative factor (1.2 default; see
  //         statistics.CORRELATION_INFLATION_FACTOR). The inflation can
  //         be overridden via config.PROJECTION_CORRELATION_INFLATION for
  //         customers with measured correlation structure.
  const crit = effectiveCritValue(samples.length, config.PROJECTION_BAND_Z);
  const corrFactor = Number.isFinite(config.PROJECTION_CORRELATION_INFLATION) && config.PROJECTION_CORRELATION_INFLATION > 0
    ? config.PROJECTION_CORRELATION_INFLATION
    : CORRELATION_INFLATION_FACTOR;
  const sumSE = s * Math.sqrt(samples.length) * corrFactor;
  const halfWidth = crit * sumSE;
  const band = {
    point: teamPoint,
    low: teamPoint - halfWidth,
    high: teamPoint + halfWidth,
    method: 'gaussian',
    sampleSize: samples.length,
  };

  // Throughput can't be negative — truncate low at 0.
  const truncatedBand = truncate(band, 0);

  return fromStatsBand(truncatedBand, confidence, 'items/week', now);
}
